'use client';

import clsx from 'clsx';
import { useFormikContext } from 'formik';
import dynamic from 'next/dynamic';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';

import { PageProductListingFilters } from 'src/data/Contentful/ContentfulGetPageProductListing.types';
import { IconAncap } from 'src/general/Icons/IconAncap';
import { IconBodyType } from 'src/general/Icons/IconBodyType';
import { IconCalendar } from 'src/general/Icons/IconCalendar';
import { IconColour } from 'src/general/Icons/IconColour';
import { IconDoor } from 'src/general/Icons/IconDoor';
import { IconDriveType } from 'src/general/Icons/IconDriveType';
import { IconEmissions } from 'src/general/Icons/IconEmissions';
import { IconFeatures } from 'src/general/Icons/IconFeatures';
import { IconMakeModel } from 'src/general/Icons/IconMakeModel';
import { IconManualTransmission } from 'src/general/Icons/IconManualTransmission';
import { IconMeter } from 'src/general/Icons/IconMeter';
import { IconPetrol } from 'src/general/Icons/IconPetrol';
import { IconPrice } from 'src/general/Icons/IconPrice';
import { IconSeat } from 'src/general/Icons/IconSeat';
import { IconShow } from 'src/general/Icons/IconShow';
import { Accordion } from 'src/general/components/Accordion/Accordion';
import { AccordionItem } from 'src/general/components/Accordion/Accordion.type';
import { InputCheckboxFormik } from 'src/general/components/forms/Input/InputCheckboxFormik';
import { InputSelectFormik } from 'src/general/components/forms/Input/InputSelectFormik';
import { InputSwitchFormik } from 'src/general/components/forms/Input/InputSwitchFormik';
import { plpStore } from 'src/stores/plpStore';
import { FilterListPayload } from 'src/types/CarFilters.types';
import { getOr } from 'src/utils/getOr';
import { plpHasFilter } from 'src/utils/plpHasFilter';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import styles from './PlpFilters.module.scss';
import { FilterOptions } from './PlpFilters.type';
import { PlpFiltersHierarchyConditionalProps } from './PlpFiltersHierarchy.types';
import { PlpFiltersRange } from './PlpFiltersRange';
import { PlpFiltersRangePrice } from './PlpFiltersRangePrice';

type Props = {
  idPrepend: string;
  filterOptions?: FilterOptions;
  filterData?: FilterListPayload;
  driveTrainHelper?: ReactNode;
} & PlpFiltersHierarchyConditionalProps;

const PlpFiltersMake = dynamic(() => import('./PlpFiltersMake').then((mod) => mod.PlpFiltersMake), {
  ssr: false,
});
const PlpFiltersHierarchy = dynamic(() => import('./PlpFiltersHierarchy').then((mod) => mod.PlpFiltersHierarchy), {
  ssr: false,
});

export const PlpFilters: FC<Props> = ({
  filterOptions = {} as FilterOptions,
  idPrepend,
  driveTrainHelper,
  ...props
}) => {
  const { values: filters } = useFormikContext<PageProductListingFilters>();
  const { setDriveTrainModalOpen } = plpStore(
    (state) => ({
      setDriveTrainModalOpen: state.setDriveTrainModalOpen,
    }),
    shallow,
  );

  const onClickDrivetrain = useCallback(() => {
    setDriveTrainModalOpen(true);
    pushToDataLayer({
      event: 'read_more',
      location: 'filters',
      text: 'driveType explained',
      type: 'button',
    });
  }, [setDriveTrainModalOpen]);

  const filterMakeModel: AccordionItem = useMemo(
    () => ({
      id: 'makeModel',
      header: (
        <FilterName
          icon={<IconMakeModel />}
          label="Make & Model"
          hasNotification={plpHasFilter('makeModel', filters)}
        />
      ),
      content: (
        <PlpFiltersMake
          id={`${idPrepend}-makeModel`}
          data-testid={`${idPrepend}-makeModel`}
          name="makeModel"
          options={getOr(filterOptions, 'makeModel', [])}
          subCategoryLabel="Model"
          {...props}
        />
      ),
    }),
    [filters.makeModel, filterOptions.makeModel],
  );
  const filterBodyType: AccordionItem = useMemo(
    () => ({
      id: 'bodyType',
      header: (
        <FilterName icon={<IconBodyType />} label="Body type" hasNotification={plpHasFilter('bodyType', filters)} />
      ),
      content: (
        <PlpFiltersHierarchy
          id={`${idPrepend}-bodyType`}
          data-testid={`${idPrepend}-bodyType`}
          name="bodyType"
          options={getOr(filterOptions, 'bodyType', [])}
          subCategoryLabel="Size"
          {...props}
        />
      ),
    }),
    [filters.bodyType, filterOptions.bodyType],
  );
  const filterPrice: AccordionItem = useMemo(
    () => ({
      id: 'price',
      header: (
        <FilterName
          icon={<IconPrice />}
          label="Price"
          hasNotification={
            plpHasFilter('priceCashMin', filters) ||
            plpHasFilter('priceCashMax', filters) ||
            plpHasFilter('priceFinMin', filters) ||
            plpHasFilter('priceFinMax', filters)
          }
        />
      ),
      content: (
        <PlpFiltersRangePrice
          optionsCash={getOr(filterOptions, 'priceCash', [])}
          optionsFinance={getOr(filterOptions, 'priceFinance', [])}
        />
      ),
    }),
    [
      filters.priceCashMin,
      filters.priceCashMax,
      filters.priceFinMin,
      filters.priceFinMax,
      filterOptions.priceCash,
      filterOptions.priceFinance,
    ],
  );
  const filterYear: AccordionItem = useMemo(
    () => ({
      id: 'year',
      header: (
        <FilterName
          icon={<IconCalendar />}
          label="Year"
          hasNotification={plpHasFilter('yearMin', filters) || plpHasFilter('yearMax', filters)}
        />
      ),
      content: <PlpFiltersRange minName="yearMin" maxName="yearMax" options={getOr(filterOptions, 'year', [])} />,
    }),
    [filters.yearMin, filters.yearMax, filterOptions.year],
  );
  const filterKilometers: AccordionItem = useMemo(
    () => ({
      id: 'kilometers',
      header: (
        <FilterName
          icon={<IconMeter />}
          label="Kilometers"
          hasNotification={plpHasFilter('kmMin', filters) || plpHasFilter('kmMax', filters)}
        />
      ),
      content: <PlpFiltersRange minName="kmMin" maxName="kmMax" options={getOr(filterOptions, 'kilometers', [])} />,
    }),
    [filters.kmMin, filters.kmMax, filterOptions.kilometers],
  );
  const filterFuelType: AccordionItem = useMemo(
    () => ({
      id: 'fuelType',
      header: (
        <FilterName icon={<IconPetrol />} label="Fuel type" hasNotification={plpHasFilter('fuelType', filters)} />
      ),
      content: (
        <InputCheckboxFormik
          id={`${idPrepend}-fuelType`}
          data-testid={`${idPrepend}-fuelType`}
          name="fuelType"
          options={getOr(filterOptions, 'fuelType', [])}
        />
      ),
    }),
    [filters.fuelType, filterOptions.fuelType],
  );
  const filterHighlightedFeatures: AccordionItem = useMemo(
    () => ({
      id: 'highlightedFeatures',
      header: (
        <FilterName
          icon={<IconFeatures />}
          label="Features"
          hasNotification={plpHasFilter('highlightedFeatures', filters)}
        />
      ),
      content: (
        <InputCheckboxFormik
          id={`${idPrepend}-highlightedFeatures`}
          data-testid={`${idPrepend}-highlightedFeatures`}
          name="highlightedFeatures"
          options={getOr(filterOptions, 'highlightedFeatures', [])}
        />
      ),
    }),
    [filters.highlightedFeatures, filterOptions.highlightedFeatures],
  );
  const filterTransmission: AccordionItem = useMemo(
    () => ({
      id: 'transmission',
      header: (
        <FilterName
          icon={<IconManualTransmission />}
          label="Transmission"
          hasNotification={plpHasFilter('transmission', filters)}
        />
      ),
      content: (
        <InputCheckboxFormik
          id={`${idPrepend}-transmission`}
          data-testid={`${idPrepend}-transmission`}
          name="transmission"
          options={getOr(filterOptions, 'transmission', [])}
        />
      ),
    }),
    [filters.transmission, filters.transmission],
  );
  const filterDriveType: AccordionItem = useMemo(
    () => ({
      id: 'driveType',
      header: (
        <FilterName icon={<IconDriveType />} label="Drivetrain" hasNotification={plpHasFilter('driveType', filters)} />
      ),
      content: (
        <>
          <InputCheckboxFormik
            id={`${idPrepend}-driveType`}
            data-testid={`${idPrepend}-driveType`}
            name="driveType"
            options={getOr(filterOptions, 'driveType', [])}
          />
          <div className={styles.filterModalButtonWrapper}>{driveTrainHelper}</div>
        </>
      ),
    }),
    [filters.driveType, filterOptions.driveType, onClickDrivetrain],
  );
  const filterSeats: AccordionItem = useMemo(
    () => ({
      id: 'seats',
      header: <FilterName icon={<IconSeat />} label="Seats" hasNotification={plpHasFilter('seats', filters)} />,
      content: (
        <InputCheckboxFormik
          id={`${idPrepend}-seats`}
          data-testid={`${idPrepend}-seats`}
          name="seats"
          options={getOr(filterOptions, 'seats', [])}
        />
      ),
    }),
    [filters.seats, filterOptions.seats],
  );
  const filterDoors: AccordionItem = useMemo(
    () => ({
      id: 'doors',
      header: <FilterName icon={<IconDoor />} label="Doors" hasNotification={plpHasFilter('doors', filters)} />,
      content: (
        <InputCheckboxFormik
          id={`${idPrepend}-doors`}
          data-testid={`${idPrepend}-doors`}
          name="doors"
          options={getOr(filterOptions, 'doors', [])}
        />
      ),
    }),
    [filters.doors, filterOptions.doors],
  );
  const filterColour: AccordionItem = useMemo(
    () => ({
      id: 'colour',
      header: <FilterName icon={<IconColour />} label="Colour" hasNotification={plpHasFilter('colour', filters)} />,
      content: (
        <InputCheckboxFormik
          id={`${idPrepend}-colour`}
          data-testid={`${idPrepend}-colour`}
          name="colour"
          options={getOr(filterOptions, 'colour', [])}
        />
      ),
    }),
    [filters.colour, filterOptions.colour],
  );
  const filterANCAP: AccordionItem = useMemo(
    () => ({
      id: 'ancapSafetyRating',
      header: (
        <FilterName
          icon={<IconAncap />}
          label="ANCAP safety rating"
          hasNotification={plpHasFilter('ancapSafetyRating', filters)}
        />
      ),
      content: (
        <InputSelectFormik
          name="ancapSafetyRating"
          id="ancapSafetyRating"
          data-testid="ancapSafetyRating"
          options={getOr(filterOptions, 'ancapSafetyRating', [])}
          size="small"
        />
      ),
    }),
    [filters.ancapSafetyRating, filterOptions.ancapSafetyRating],
  );
  const filterCO2: AccordionItem = useMemo(
    () => ({
      id: 'co2Emissions',
      header: (
        <FilterName
          icon={<IconEmissions />}
          label="CO2 emissions"
          hasNotification={plpHasFilter('co2Emissions', filters)}
        />
      ),
      content: (
        <InputSelectFormik
          name="co2Emissions"
          id="co2Emissions"
          data-testid="co2Emissions"
          options={getOr(filterOptions, 'co2Emissions', [])}
          size="small"
        />
      ),
    }),
    [filters.co2Emissions, filterOptions.co2Emissions],
  );

  const filterItems: AccordionItem[] = [
    filterMakeModel,
    filterBodyType,
    filterPrice,
    filterYear,
    filterKilometers,
    filterFuelType,
    filterHighlightedFeatures,
    filterTransmission,
    filterDriveType,
    filterSeats,
    filterDoors,
    filterColour,
    filterANCAP,
    filterCO2,
  ];

  return (
    <div className={styles.root} data-testid="PlpFilters-root">
      <Accordion items={filterItems} initialOpenIndexes={[0]} />
      <AvailableFilter />
    </div>
  );
};

const FilterName: FC<{ icon: ReactNode; label: string; hasNotification?: boolean }> = ({
  icon,
  label,
  hasNotification,
}) => (
  <div className={styles.filterName}>
    {icon}
    <span className={clsx('c-fs-body1', 'c-fw-bold')}>{label}</span>
    {hasNotification && <span className={styles.filterNotification} />}
  </div>
);

const AvailableFilter: FC = () => (
  <div className={styles.filterSwitchWrapper}>
    <IconShow />
    <InputSwitchFormik
      id="state"
      name="state"
      options={[{ label: 'Show available only', value: 'available' }]}
      inputPlacement="end"
      className={styles.filterSwitch}
    />
  </div>
);
